import { Box, Button, Modal, Typography } from "@mui/material";
import StyledBarChart from "../chart/BarChart";
import ChartCheckbox from "../chart/ChartCheckbox";
import { useContext, useState, useMemo, useEffect } from "react";
import { DashboardContext } from "../../contexts/DashboardContext";
import { BarSeriesType } from "@mui/x-charts";
import { MakeOptional } from "@mui/x-charts/models/helpers";
import { useTranslation } from "react-i18next";
import { EmissionData } from "../../models/Emission";
import Group from "../../models/Group";
import { common, overview } from "../../i18n";
import {
  BusinessEntityDetail,
  BusinessEntitySummary,
  SCOPE_MAPPING,
} from "../../models/emission-dashboard.types";

// Constants สำหรับการจัดเรียงและประเภทการดูข้อมูล
export const highToLow = 0;
export const LowToHigh = 1;
export const byOrg = 2;
export const bySite = 1;

interface Props {
  groups: Group[];
  getGroup: (emission: EmissionData) => string | undefined;
  by: number;
  sort: number;
  top?: number;
  scope?: "Scope-1" | "Scope-2" | "Scope-3" | "Outside-of-Scope";
}

const EmissionByBusinessGroup = (props: Props) => {
  const { dashboardData } = useContext(DashboardContext);
  const { t } = useTranslation([common, overview], { nsMode: "fallback" });

  const [checkRecord, setCheckRecord] = useState<Record<string, boolean>>(
    () => {
      const record: Record<string, boolean> = {};
      props.groups.forEach((group) => {
        record[group.id] = true;
      });
      return record;
    },
  );

  const [openSeeMore, setOpenSeeMore] = useState(false);

  // --- 1. Process Data (Sort, Filter, Slice) ---
  const { chartLabels, chartSeries } = useMemo(() => {
    if (!dashboardData?.byBusinessStructure) {
      return { chartLabels: [], chartSeries: [], totalValue: 0 };
    }

    let rawList: (BusinessEntitySummary | BusinessEntityDetail)[] = [];

    if (props.scope && SCOPE_MAPPING[props.scope]) {
      const scopeKey = SCOPE_MAPPING[props.scope];
      const scopeData = dashboardData.byScopes[scopeKey];
      rawList =
        props.by === byOrg
          ? scopeData.byBusinessStructure.subOrganizations
          : scopeData.byBusinessStructure.sites;
    } else {
      rawList =
        props.by === byOrg
          ? dashboardData.byBusinessStructure.subOrganizations
          : dashboardData.byBusinessStructure.sites;
    }

    const listWithValue = rawList.map((item) => {
      let sortValue = 0;
      if ("summary" in item) {
        if (props.scope) {
          const key = SCOPE_MAPPING[props.scope];
          sortValue = item.summary[key]?.value ?? 0;
        } else {
          sortValue = item.summary.total;
        }
      } else {
        sortValue = item.value;
      }

      let label = item.name;
      if (props.by === bySite && item.subOrg) {
        label = `${item.name} : ${item.subOrg}`;
      }

      return { ...item, label, sortValue };
    });

    const activeList = listWithValue.filter((item) => item.sortValue > 0);

    activeList.sort((a, b) => {
      return props.sort === highToLow
        ? b.sortValue - a.sortValue
        : a.sortValue - b.sortValue;
    });

    const processedList = props.top
      ? activeList.slice(0, props.top)
      : activeList;
    const labels = processedList.map((item) => item.label);
    const series: MakeOptional<BarSeriesType, "type">[] = [];
    let calculatedTotal = 0;

    props.groups.forEach((group) => {
      if (!props.scope && !checkRecord[group.id]) return;

      const scopeKey = props.scope
        ? SCOPE_MAPPING[props.scope]
        : SCOPE_MAPPING[group.id];
      if (!scopeKey) return;

      const dataValues = processedList.map((item) => {
        let val = 0;
        if ("summary" in item) {
          val = item.summary[scopeKey]?.value ?? 0;
        } else {
          if (!checkRecord[group.id]) return 0;
          val = item.types.find((t) => t.categoryEn === group.id)?.value ?? 0;
        }
        calculatedTotal += val;
        return val;
      });

      series.push({
        data: dataValues,
        label: t(group.title),
        stack: "total",
        color: group.colors[1],
      });
    });

    return {
      chartLabels: labels,
      chartSeries: series,
      totalValue: calculatedTotal,
    };
  }, [
    dashboardData,
    props.by,
    props.sort,
    props.top,
    props.scope,
    props.groups,
    checkRecord,
    t,
  ]);

  const checkboxs = props.groups.map((group) => (
    <ChartCheckbox
      key={group.id}
      text={t(group.title)}
      color={group.colors[1]}
      checked={checkRecord[group.id] ?? false}
      onChange={(_, checked) => {
        setCheckRecord((prev) => ({ ...prev, [group.id]: checked }));
      }}
    />
  ));

  useEffect(() => {
    const record: Record<string, boolean> = {};
    props.groups.forEach((group) => {
      record[group.id] = true;
    });
    setCheckRecord(record);
  }, [props.groups]);

  return (
    <Box>
      <Box
        display={"flex"}
        justifyContent={"flex-end"}
        gap={"24px"}
        flexWrap={"wrap"}
      >
        {checkboxs}
      </Box>
      <Box flexShrink={0} width={"100%"} sx={{ overflow: "auto" }}>
        {chartLabels.length > 0 ? (
          <StyledBarChart
            layout="horizontal"
            series={chartSeries}
            seriesLabel="tCO₂e"
            labels={chartLabels}
            left={200}
            total={
              props.scope
                ? dashboardData?.sumScopes[SCOPE_MAPPING[props.scope]].value
                : dashboardData?.sumScopes.total ?? 0
            }
          />
        ) : (
          <Box p={3} textAlign="center">
            <Typography color="textSecondary">
              {t("No Data Available")}
            </Typography>
          </Box>
        )}

        {props.top && (
          <Box>
            <Box width={"100%"} textAlign={"end"} mt={1}>
              <Button onClick={() => setOpenSeeMore(true)}>
                {t("See-More")}
              </Button>
            </Box>
            <Modal open={openSeeMore} onClose={() => setOpenSeeMore(false)}>
              <Box
                position={"absolute"}
                top={"50%"}
                left={"50%"}
                display={"flex"}
                flexDirection={"column"}
                gap={"24px"}
                p={"32px"}
                width={"70%"}
                borderRadius={"8px"}
                sx={{ transform: "translate(-50%, -50%)", background: "white" }}
                overflow={"auto"}
                maxHeight={"70dvh"}
              >
                <EmissionByBusinessGroup {...props} />
              </Box>
            </Modal>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EmissionByBusinessGroup;
